import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Banknote, Copy, Users, UserCheck, UserX } from "lucide-react";
import BottomNav from "../components/BottomNav";
import API from "../services/api";

function ReferEarn() {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [data, setData] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);

  const loadSummary = async () => {
    if (!user?.id) return;
    try {
      const res = await API.get(`/referrals/${user.id}/summary`);
      setData(res.data || null);
    } catch (err) {
      setData(null);
    }
  };

  useEffect(() => {
    if (!user?.id) {
      navigate("/login");
      return;
    }
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, user?.id]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isCompact = viewportWidth <= 390;

  const copyCode = async () => {
    const code = data?.user?.referral_code;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      alert("Referral code copied");
    } catch (_) {
      alert(code);
    }
  };

  const shareCode = async () => {
    const code = data?.user?.referral_code;
    if (!code) return;
    const url = `${window.location.origin}/login?tab=register&ref=${encodeURIComponent(code)}`;
    const text = `Join Matka Kings with my referral code ${code} and start playing.`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Matka Kings Referral", text, url });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        alert("Referral link copied");
      }
    } catch (_) {}
  };

  const claimWeekly = async () => {
    try {
      const res = await API.post(`/referrals/${user.id}/claim`);
      setData(res.data?.summary || data);
      alert("Referral bonus claimed");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to claim referral bonus");
    }
  };

  const stats = data?.stats || {};
  const settings = data?.settings || {};
  const code = data?.user?.referral_code || "-----";
  const weekLabel = stats.week_start && stats.week_end
    ? `${stats.week_start} to ${new Date(new Date(stats.week_end).getTime() - 86400000).toISOString().slice(0, 10)}`
    : "Monday to Monday";

  return (
    <div style={page}>
      <header style={header}>
        <button type="button" style={backBtn} onClick={() => navigate("/profile")}>
          <ArrowLeft size={22} />
        </button>
        <strong style={headerTitle}>Refer & Earn</strong>
      </header>

      <main style={{ ...content, padding: isCompact ? "12px" : content.padding }}>
        <section style={heroCard}>
          <div style={{ ...heroRow, flexWrap: "wrap" }}>
            <div>
              <div style={heroLabel}>Total Income</div>
              <div style={heroValue}>{`Rs ${Number(stats.total_earned || 0).toFixed(2)}`}</div>
            </div>
            <div style={heroActions}>
              <button type="button" style={roundAction} onClick={() => navigate("/profile")}>
                <Users size={20} />
              </button>
              <button type="button" style={roundAction} onClick={shareCode}>
                <Banknote size={20} />
              </button>
            </div>
          </div>

          <div style={divider} />

          <div style={codeLabel}>Referral Code</div>
          <button type="button" style={{ ...codeBox, padding: isCompact ? "14px" : codeBox.padding, flexWrap: "wrap", justifyContent: isCompact ? "center" : codeBox.justifyContent, gap: isCompact ? "10px" : undefined }} onClick={copyCode}>
            <span style={{ ...codeText, fontSize: isCompact ? 20 : codeText.fontSize, letterSpacing: isCompact ? "0.08em" : codeText.letterSpacing }}>{code}</span>
            <span style={copyHint}><Copy size={18} /> Tap to copy</span>
          </button>

          <p style={helperText}>
            Share your code with friends. Earn {Number(settings.referral_commission_percent || 3)}% on each referred player&apos;s weekly loss only. Claim opens on Monday.
          </p>
        </section>

        <section style={{ ...statsGrid, gridTemplateColumns: isCompact ? "1fr" : statsGrid.gridTemplateColumns }}>
          <StatCard title="Total" value={stats.total_referrals || 0} icon={<Users size={24} />} active />
          <StatCard title="Active" value={stats.active_referrals || 0} icon={<UserCheck size={24} />} />
          <StatCard title="Inactive" value={stats.inactive_referrals || 0} icon={<UserX size={24} />} />
        </section>

        <section style={earningsCard}>
          <div style={earningsTitle}>Referral Earnings</div>
          <div style={earningsBalance}>{`Rs ${Number(stats.available_to_claim || 0).toFixed(2)}`}</div>
          <div style={{ ...miniGrid, gridTemplateColumns: isCompact ? "1fr" : miniGrid.gridTemplateColumns }}>
            <MiniStat label="Total Earned" value={`Rs ${Number(stats.total_earned || 0).toFixed(2)}`} />
            <MiniStat label="Pending" value={`Rs ${Number(stats.available_to_claim || 0).toFixed(2)}`} />
            <MiniStat label="Redeemed" value={`Rs ${Number(stats.current_week_claimed || 0).toFixed(2)}`} />
          </div>
        </section>

        <section style={claimCard}>
          <div style={{ ...claimTop, flexWrap: "wrap" }}>
            <div>
              <div style={claimLabel}>Weekly claim window</div>
              <strong style={claimPeriod}>{weekLabel}</strong>
            </div>
            <span style={claimBadge}>{`${Number(settings.referral_commission_percent || 3)}%`}</span>
          </div>
          <div style={claimMeta}>
            <span>{`Weekly deposits: Rs ${Number(stats.current_week_deposit_amount || 0).toFixed(2)}`}</span>
            <span>{`Weekly withdraws: Rs ${Number(stats.current_week_withdraw_amount || 0).toFixed(2)}`}</span>
            <span>{`Weekly net loss: Rs ${Number(stats.current_week_loss_amount || 0).toFixed(2)}`}</span>
            <span>{`Min claim Rs ${Number(settings.referral_claim_min || 100).toFixed(2)}`}</span>
          </div>
          <button type="button" style={{ ...claimButton, opacity: stats.can_claim ? 1 : 0.45 }} disabled={!stats.can_claim} onClick={claimWeekly}>
            Claim Weekly Bonus
          </button>
          {!stats.can_claim && (
            <div style={claimHint}>
              {stats.is_claim_day ? `Min Rs ${Number(settings.referral_claim_min || 100)} weekly loss required to redeem` : "Referral bonus can be claimed on Monday only"}
            </div>
          )}
        </section>

        <section style={historyCard}>
          <div style={historyHeader}>
            <strong>My Referrals</strong>
            <span>{`${(data?.referrals || []).length} users`}</span>
          </div>
          {(data?.referrals || []).length === 0 ? (
            <div style={emptyState}>
              <Users size={52} color="#cbd5e1" />
              <div style={emptyTitle}>No referrals found</div>
              <div style={emptySubtitle}>Share your code to invite friends</div>
            </div>
          ) : (
            <div style={list}>
              {data.referrals.map((item) => (
                <div key={item.id} style={{ ...listRow, alignItems: isCompact ? "flex-start" : listRow.alignItems, flexWrap: isCompact ? "wrap" : "nowrap" }}>
                  <div>
                    <strong style={rowTitle}>{item.name}</strong>
                    <div style={rowSub}>{item.phone}</div>
                  </div>
                  <span style={item.is_active ? statusActive : statusInactive}>
                    {item.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      <BottomNav />
    </div>
  );
}

function StatCard({ title, value, icon, active }) {
  return (
    <div style={{ ...statCard, ...(active ? statCardActive : {}) }}>
      <div style={{ ...statIcon, ...(active ? statIconActive : {}) }}>{icon}</div>
      <div style={{ ...statValue, color: active ? "#fff" : "#1e293b" }}>{value}</div>
      <div style={{ ...statTitle, color: active ? "#dbeafe" : "#64748b" }}>{title}</div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div style={miniCard}>
      <div style={miniLabel}>{label}</div>
      <div style={miniValue}>{value}</div>
    </div>
  );
}

const page = { minHeight: "100vh", background: "#f8fafc", paddingBottom: "84px" };
const header = { height: "56px", background: "#fff", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: "14px", padding: "0 16px", position: "sticky", top: 0, zIndex: 20 };
const backBtn = { width: "42px", height: "42px", border: 0, borderRadius: "50%", background: "#f8fafc", display: "grid", placeItems: "center" };
const headerTitle = { fontSize: "18px", fontWeight: 800, color: "#0f172a" };
const content = { maxWidth: "540px", margin: "0 auto", padding: "16px", display: "grid", gap: "16px" };
const heroCard = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "24px", padding: "18px" };
const heroRow = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" };
const heroLabel = { color: "#64748b", fontSize: "14px" };
const heroValue = { color: "#2563eb", fontWeight: 900, fontSize: "24px", marginTop: "6px" };
const heroActions = { display: "flex", gap: "12px" };
const roundAction = { width: "44px", height: "44px", borderRadius: "50%", border: "2px solid #3b82f6", color: "#3b82f6", background: "#fff", display: "grid", placeItems: "center" };
const divider = { height: "1px", background: "#e5e7eb", margin: "18px 0" };
const codeLabel = { color: "#475569", fontSize: "15px", marginBottom: "10px" };
const codeBox = { width: "100%", minHeight: "92px", borderRadius: "20px", border: "2px dashed #60a5fa", background: "#f8fbff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 22px", color: "#2563eb" };
const codeText = { fontSize: "24px", fontWeight: 900, letterSpacing: "0.18em" };
const copyHint = { display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: 700 };
const helperText = { margin: "18px 0 0", textAlign: "center", color: "#94a3b8", fontSize: "14px", lineHeight: 1.5 };
const statsGrid = { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "12px" };
const statCard = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "20px", padding: "18px 12px", textAlign: "center" };
const statCardActive = { background: "#3b82f6", borderColor: "#3b82f6" };
const statIcon = { width: "40px", height: "40px", borderRadius: "50%", margin: "0 auto 10px", display: "grid", placeItems: "center", color: "#475569" };
const statIconActive = { color: "#fff" };
const statValue = { fontSize: "22px", fontWeight: 900 };
const statTitle = { marginTop: "6px", fontSize: "14px" };
const earningsCard = { background: "linear-gradient(135deg,#3b82f6,#2563eb)", borderRadius: "24px", color: "#fff", padding: "18px" };
const earningsTitle = { fontSize: "14px", opacity: 0.92 };
const earningsBalance = { fontSize: "24px", fontWeight: 900, marginTop: "8px" };
const miniGrid = { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "12px", marginTop: "16px" };
const miniCard = { background: "rgba(255,255,255,0.10)", borderRadius: "18px", padding: "14px 10px", textAlign: "center" };
const miniLabel = { fontSize: "12px", opacity: 0.86 };
const miniValue = { marginTop: "8px", fontWeight: 800, fontSize: "14px" };
const claimCard = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "24px", padding: "18px" };
const claimTop = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" };
const claimLabel = { color: "#64748b", fontSize: "13px" };
const claimPeriod = { display: "block", marginTop: "6px", color: "#0f172a" };
const claimBadge = { background: "#eff6ff", color: "#2563eb", borderRadius: "999px", padding: "8px 12px", fontWeight: 800 };
const claimMeta = { display: "grid", gap: "6px", color: "#64748b", fontSize: "13px", marginTop: "14px" };
const claimButton = { width: "100%", height: "52px", marginTop: "16px", border: 0, borderRadius: "18px", background: "#3b82f6", color: "#fff", fontWeight: 800, fontSize: "16px" };
const claimHint = { marginTop: "12px", background: "#f8fafc", color: "#9ca3af", borderRadius: "18px", padding: "14px 16px", fontSize: "14px", textAlign: "center" };
const historyCard = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "24px", overflow: "hidden" };
const historyHeader = { padding: "18px", display: "flex", justifyContent: "space-between", alignItems: "center", color: "#475569", borderBottom: "1px solid #f1f5f9" };
const emptyState = { padding: "34px 18px", textAlign: "center", display: "grid", justifyItems: "center", gap: "10px" };
const emptyTitle = { fontSize: "18px", color: "#475569", fontWeight: 700 };
const emptySubtitle = { color: "#94a3b8", fontSize: "14px" };
const list = { display: "grid" };
const listRow = { padding: "14px 18px", borderBottom: "1px solid #f8fafc", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" };
const rowTitle = { color: "#0f172a" };
const rowSub = { color: "#94a3b8", fontSize: "13px", marginTop: "4px" };
const statusActive = { background: "#dcfce7", color: "#15803d", padding: "8px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 700 };
const statusInactive = { background: "#f1f5f9", color: "#64748b", padding: "8px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 700 };

export default ReferEarn;
